import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

import { useBreakpoint } from "../WidgetBreakpoint";
import { useTranslation } from "react-i18next";
import parse from "html-react-parser";

import { Colours } from "@learningblocks/shared-components";

import { makeStyles } from "@material-ui/core/styles";
import Slider from "@material-ui/core/Slider";
import { createMuiTheme } from "@material-ui/core/styles";
import { ThemeProvider } from "@material-ui/styles";

import BarChart4 from "./BarChart4";

export default function Widget(props) {
  const {
    t,
    // i18n
  } = useTranslation("translation");

  // Include the widget providers
  const breakpoints = useBreakpoint();

  const [chartSelection, setChartSelection] = useState(1);
  const [sampleSize, setSampleSize] = useState(10);
  const [display, setDisplay] = useState("histogram");
  const [loaded, setLoaded] = useState();
  const [widgetArea] = useState({
    height: 0,
    width: 0,
  });

  useEffect(() => {
    require([`./locales/${props.i18next.language}/translation.json`], function (
      data
    ) {
      props.i18next.addResourceBundle("en", "translation", data, true, true);
      setLoaded(true);
    });
  }, []);

  const selected = {
    borderBottom: "3px solid #ffaa27",
    color: "white",
    background: "transparent",
    padding: "8px",
    width: "50%",
    textAlign: "center",
    cursor: "pointer",
  };

  const notSelected = {
    borderBottom: "1px solid #ffaa27",
    width: "50%",
    background: "transparent",
    color: "white",
    padding: "8px",
    textAlign: "center",
    cursor: "pointer",
  };

  const useStyles = makeStyles((theme) => ({
    root: {
      width: "calc(100% - 40px)",
      margin: "auto",
    },
    margin: {
      height: theme.spacing(3),
    },
  }));

  const muiTheme = createMuiTheme({
    overrides: {
      MuiSlider: {
        thumb: {
          height: "1px",
          width: "1px",
          color: "#ffaa27",
        },
        track: {
          height: "5px",
          color: "#ffaa27",
        },
        rail: {
          height: "5px",
          color: "white",
        },
        mark: {
          height: "8px",
          color: "white",
        },
        markLabel: {
          color: "white",
          fontSize: "12px",
          marginLeft: "1px",
        },
        markLabelActive: {
          color: "white",
        },
        valueLabel: {
          top: 7,
          left: -9,
          background: "#ffaa27",
          borderRadius: "10px",
          height: "20px",
          "& *": {
            marginTop: -4,
            marginLeft: 1,
            background: "transparent",
            color: "#003359",
          },
        },
      },
    },
  });

  function handleSliderChange(value) {
    setSampleSize(value);
  }

  const marks = [
    { value: 100, label: "100" },
    { value: 200, label: "200" },
    { value: 300, label: "300" },
    { value: 400, label: "400" },
    { value: 500, label: "500" },
    { value: 600, label: "600" },
    { value: 700, label: "700" },
    { value: 800, label: "800" },
    { value: 900, label: "900" },
    { value: 1000, label: "1000" },
    { value: 150, label: "" },
    { value: 250, label: "" },
    { value: 350, label: "" },
    { value: 450, label: "" },
    { value: 550, label: "" },
    { value: 650, label: "" },
    { value: 750, label: "" },
    { value: 850, label: "" },
    { value: 950, label: "" },
  ];

  const smallMarks = [
    { value: 100, label: "100" },
    { value: 200, label: "" },
    { value: 300, label: "" },
    { value: 400, label: "400" },
    { value: 500, label: "" },
    { value: 600, label: "" },
    { value: 700, label: "700" },
    { value: 800, label: "" },
    { value: 900, label: "" },
    { value: 1000, label: "1000" },
  ];

  const classes = useStyles();

  const handleEnter = () => {
    if (event.keyCode === 13) {
      document.activeElement.click();
    }
  };

  const setDisplayTab = (tab) => {
    setDisplay(tab);
  };

  return (
    <>
      <div
        style={{
          display: props.intro === "" ? "none" : null,
          border: "1px solid white",
          borderRadius: "5px",
          padding: "20px",
          color: "white",
          margin: "20px",
        }}
      >
        {parse(t(props.intro))}
      </div>
      <div
        className="widgetWrapper"
        style={{
          width: "100%",
          overflow: "hidden",
          display: loaded ? "flex" : "none",
          flexDirection: "column",
          // border: "1px solid red",
        }}
      >
        <div
          className="altTxt field"
          aria-label={t(
            "A histogram with a sliding bar to determine sample size appears. There are three options for the histogram, each using a different rule to determine bin number or width. As you drag the slider to the right, the sample size or number of data points increases. This has two effects on the histograms, depending on which rule is used for determining the bin number or bin width. In general, increasing the sample size results in more bins that have narrower bin widths. Each rule has different sensitivity to the sample size, with some rules responding more quickly to increasing sample size. While the number of bins remains stable, an increase in sample size will generally lead to higher frequencies within each bin. Thus, as you drag the slider to the right, the bins tend to become more numerous, narrower, and taller, but each rule will implement these changes to a different degree. All of the histograms show the same overall picture of data centered near the value 50. This is where the highest frequencies are shown. The frequencies of data points drop off from there towards the value 0 and the value 100."
          )}
          style={{
            height: "0px",
            overflow: "hidden",
          }}
        >
          {t(
            "A histogram with a sliding bar to determine sample size appears. There are three options for the histogram, each using a different rule to determine bin number or width. As you drag the slider to the right, the sample size or number of data points increases. This has two effects on the histograms, depending on which rule is used for determining the bin number or bin width. In general, increasing the sample size results in more bins that have narrower bin widths. Each rule has different sensitivity to the sample size, with some rules responding more quickly to increasing sample size. While the number of bins remains stable, an increase in sample size will generally lead to higher frequencies within each bin. Thus, as you drag the slider to the right, the bins tend to become more numerous, narrower, and taller, but each rule will implement these changes to a different degree. All of the histograms show the same overall picture of data centered near the value 50. This is where the highest frequencies are shown. The frequencies of data points drop off from there towards the value 0 and the value 100."
          )}
        </div>
        <div
          className="ctrl-title"
          style={{
            // fontWeight: "bold",
            width: "100%",
            textAlign: "center",
            color: "white",
            padding: "16px 0px 5px 0px",
          }}
        >
          {t("Sample size")}
        </div>
        <div
          className="chart-type-select"
          style={{
            background: "#002F52",
            maxWidth: "1000px",
            margin: "auto",
            border: "1px solid " + Colours.navy4,
            boxSizing: "border-box",
            borderRadius: "5px",
            display: widgetArea.height < 678 ? "flex" : "none",
            flexDirection: "row",
            width: "calc(100% - 20px)",
          }}
        >
          <div className={classes.root}>
            <ThemeProvider theme={muiTheme}>
              <Slider
                min={100}
                max={1000}
                defaultValue={10}
                getAriaValueText={handleSliderChange}
                aria-labelledby="discrete-slider-custom"
                step={10}
                marks={breakpoints.xs || breakpoints.sm ? smallMarks : marks}
                valueLabelDisplay="on"
              />
            </ThemeProvider>
          </div>
        </div>
        <div
          className="chart-type-select"
          style={{
            maxWidth: "1000px",
            margin: "auto",
            marginTop: "16px",
            display: widgetArea.height < 678 ? "flex" : "none",
            flexDirection: "row",
            boxSizing: "border-box",
            width: "calc(100% - 20px)",
            // overflowY: "scroll",
            scrollbarWidth: "none",
          }}
        >
          <div
            style={chartSelection == 1 ? selected : notSelected}
            aria-label={
              chartSelection == 1
                ? `Histogram using rule 1 selected`
                : `Histogram using rule 1 unselected`
            }
            onClick={() => setChartSelection(1)}
            tabIndex="0"
            onKeyPress={() => handleEnter(event)}
          >
            {t("Rule {{number}}", { number: 1 })}
          </div>
          <div
            style={chartSelection == 2 ? selected : notSelected}
            aria-label={
              chartSelection == 2
                ? `Histogram using rule 2 selected`
                : `Histogram using rule 2 unselected`
            }
            onClick={() => setChartSelection(2)}
            tabIndex="0"
            onKeyPress={() => handleEnter(event)}
          >
            {t("Rule {{number}}", { number: 2 })}
          </div>
          <div
            style={chartSelection == 3 ? selected : notSelected}
            aria-label={
              chartSelection == 3
                ? `Histogram using rule 3 selected`
                : `Histogram using rule 3 unselected`
            }
            onClick={() => setChartSelection(3)}
            tabIndex="0"
            onKeyPress={() => handleEnter(event)}
          >
            {t("Rule {{number}}", { number: 3 })}
          </div>
        </div>
        <div
          className="widgetCharts"
          style={{
            position: "relative",
            display: "flex",
            width: breakpoints.md ? "calc(100% - 32px)" : "calc(100% - 96px)",
            maxWidth: "1000px",
            height: "calc(100% - 32px)",
            padding: breakpoints.md ? "16px" : "32px",
            overflowY: "hidden",
            alignItems: "center",
            flexDirection: "column",
            justifyContent: "space-around",
            margin: "0 auto",
            // border: "1px solid red",
          }}
        >
          <BarChart4
            width={
              breakpoints.xs
                ? 200
                : breakpoints.sm
                  ? 250
                  : breakpoints.md
                    ? 500
                    : 500
            }
            height={
              breakpoints.xs
                ? 250
                : breakpoints.sm
                  ? 250
                  : breakpoints.md
                    ? 300
                    : 300
            }
            widgetData={props.widgetData}
            hist={chartSelection}
            visible={props.visible}
            sampleSize={sampleSize}
            display={display}
            setDisplay={setDisplayTab}
          />
        </div>
      </div>
    </>
  );
}

Widget.propTypes = {
  widgetData: PropTypes.object,
  inline: PropTypes.boolean,
  visible: PropTypes.boolean,
  usersnap: PropTypes.string,
};
